import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  BarChart3,
  AlertTriangle,
  Package,
  RefreshCw,
  Sparkles,
  TrendingUp,
  ShoppingCart,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { TRACKING_EVENTS } from "../../constants/trackingEvents";
import { api } from "../../lib/api";
import { trackEvent } from "../../lib/tracking";
import { useAuth } from "../../providers/AuthProvider";
import { formatInrIntl } from "../../lib/currency";
import { AtGlanceInsights } from "../components/analytics/AtGlanceInsights";

export function AnalyticsPage() {
  const { token } = useAuth();
  const [windowDays, setWindowDays] = useState(30);
  const [leadTimeInput, setLeadTimeInput] = useState(7);
  const [safetyInput, setSafetyInput] = useState(0);
  const [settingsSaved, setSettingsSaved] = useState<{ lead: number; safety: number } | null>(null);

  const [reorder, setReorder] = useState<Awaited<ReturnType<typeof api.analytics.getReorderSuggestions>> | null>(
    null,
  );
  const [growth, setGrowth] = useState<Awaited<ReturnType<typeof api.analytics.getGrowthInsights>> | null>(null);
  const [peer, setPeer] = useState<Awaited<ReturnType<typeof api.analytics.getPeerRoadmap>> | null>(null);
  const [atGlance, setAtGlance] = useState<
    NonNullable<Awaited<ReturnType<typeof api.analytics.getAnalyticsSummary>>["data"]["at_glance"]> | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const analyticsOpenedRef = useRef(false);
  const growthInsightViewedRef = useRef(false);

  useEffect(() => {
    if (analyticsOpenedRef.current) return;
    analyticsOpenedRef.current = true;
    trackEvent(TRACKING_EVENTS.ANALYTICS_OPENED);
  }, []);

  useEffect(() => {
    if (growthInsightViewedRef.current) return;
    if (!growth) return;
    const hasMomentum = (growth.category_momentum?.length ?? 0) > 0;
    const hasAffinity = (growth.basket_affinity?.length ?? 0) > 0;
    const hasTopMovers = (growth.top_movers_low_cover?.length ?? 0) > 0;
    if (hasMomentum || hasAffinity || hasTopMovers) {
      growthInsightViewedRef.current = true;
      trackEvent(TRACKING_EVENTS.GROWTH_INSIGHT_VIEWED);
    }
  }, [growth]);

  const fetchAnalytics = useCallback(
    async (opts: { lead: number; safety: number }) => {
      if (!token) return;
      setLoading(true);
      setErr(null);
      try {
        const [r, g, p, glance] = await Promise.all([
          api.analytics.getReorderSuggestions({
            token,
            query: {
              window_days: windowDays,
              lead_time_days: opts.lead,
              safety_stock_days: opts.safety,
            },
          }),
          api.analytics.getGrowthInsights({ token, query: { window_days: windowDays } }),
          api.analytics.getPeerRoadmap({ token }),
          api.analytics
            .getAnalyticsSummary({
              token,
              query: {
                window_days: windowDays,
                lead_time_days: opts.lead,
                safety_stock_days: opts.safety,
              },
            })
            .then((res) => res.data.at_glance)
            .catch(() => null),
        ]);
        setReorder(r);
        setGrowth(g);
        setPeer(p);
        setAtGlance(glance);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    },
    [token, windowDays],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const s = await api.analytics.getSettings({ token });
        if (cancelled) return;
        const lead = s.data.default_supplier_lead_time_days;
        const safety = s.data.safety_stock_days;
        setLeadTimeInput(lead);
        setSafetyInput(safety);
        setSettingsSaved({ lead, safety });
        const [r, g, p, glance] = await Promise.all([
          api.analytics.getReorderSuggestions({
            token,
            query: { window_days: windowDays, lead_time_days: lead, safety_stock_days: safety },
          }),
          api.analytics.getGrowthInsights({ token, query: { window_days: windowDays } }),
          api.analytics.getPeerRoadmap({ token }),
          api.analytics
            .getAnalyticsSummary({
              token,
              query: {
                window_days: windowDays,
                lead_time_days: lead,
                safety_stock_days: safety,
              },
            })
            .then((res) => res.data.at_glance)
            .catch(() => null),
        ]);
        if (cancelled) return;
        setReorder(r);
        setGrowth(g);
        setPeer(p);
        setAtGlance(glance);
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, windowDays]);

  const saveSettings = async () => {
    if (!token) return;
    try {
      await api.analytics.updateSettings({
        token,
        body: {
          default_supplier_lead_time_days: leadTimeInput,
          safety_stock_days: safetyInput,
        },
      });
      setSettingsSaved({ lead: leadTimeInput, safety: safetyInput });
      await fetchAnalytics({ lead: leadTimeInput, safety: safetyInput });
    } catch {
      setErr("Could not save settings");
    }
  };

  const refreshWithInputs = () => fetchAnalytics({ lead: leadTimeInput, safety: safetyInput });

  const severityBadge = (s: string) => {
    if (s === "critical") return <Badge variant="destructive">Order now</Badge>;
    if (s === "warning") return <Badge className="bg-amber-500 hover:bg-amber-600">Reorder soon</Badge>;
    if (s === "slow_mover") return <Badge variant="secondary">Slow mover</Badge>;
    return <Badge variant="outline">OK</Badge>;
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <section className="space-y-4" aria-label="Analytics overview">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Analytics</h1>
            <p className="text-muted-foreground max-w-2xl">
              Reorder risk from sales velocity, lead time, and growth signals from your own store data.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 self-start sm:mt-1"
            onClick={refreshWithInputs}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {err ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {err}
          </div>
        ) : null}

        <AtGlanceInsights variant="strip" insights={atGlance} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4" />
              Replenishment settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Default supplier lead time and safety buffer feed reorder alerts. Saved to your business profile.
            </p>
            <div className="space-y-2">
              <Label htmlFor="lead">Lead time (days)</Label>
              <Input
                id="lead"
                type="number"
                min={1}
                max={90}
                value={leadTimeInput}
                onChange={(e) => setLeadTimeInput(Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="safety">Safety stock (days of demand)</Label>
              <Input
                id="safety"
                type="number"
                min={0}
                max={90}
                value={safetyInput}
                onChange={(e) => setSafetyInput(Number(e.target.value))}
              />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={saveSettings}>
                Save settings
              </Button>
              <Button type="button" variant="secondary" className="flex-1" onClick={refreshWithInputs}>
                Preview
              </Button>
            </div>
            {settingsSaved ? (
              <p className="text-xs text-muted-foreground">
                Saved defaults: {settingsSaved.lead}d lead, {settingsSaved.safety}d safety buffer.
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analysis window
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="win">Sales history window (days)</Label>
              <Input
                id="win"
                type="number"
                min={7}
                max={90}
                className="w-full max-w-[10rem] sm:w-40"
                value={windowDays}
                onChange={(e) => setWindowDays(Number(e.target.value))}
              />
            </div>
            <p className="min-w-0 flex-1 pb-2 text-xs text-muted-foreground md:min-w-[12rem]">
              Changing the window reloads analytics. Use Preview to test lead/safety without saving.
            </p>
            {reorder?.meta ? (
              <p className="text-xs text-muted-foreground w-full">
                Last run: {new Date(reorder.meta.generated_at).toLocaleString()} · Lead {reorder.meta.lead_time_days}d ·
                Safety {reorder.meta.safety_stock_days}d · Observation {reorder.meta.observation_days}d
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Reorder and stock risk
          </CardTitle>
          <Button size="sm" asChild>
            <Link
              to="/purchase-orders/new"
              onClick={() => trackEvent(TRACKING_EVENTS.REORDER_SUGGESTION_CLICKED)}
            >
              New purchase order
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {loading && !reorder ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : (
            <div className="min-w-0 overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">On hand</TableHead>
                    <TableHead className="text-right">Avg daily sales</TableHead>
                    <TableHead className="text-right">Days cover</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead className="text-right">Suggested qty</TableHead>
                    <TableHead>Order by</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reorder?.rows ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No products to analyze yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    reorder!.rows.map((row) => (
                      <TableRow key={row.product_id}>
                        <TableCell className="min-w-0 max-w-[min(14rem,45vw)] truncate font-medium sm:max-w-xs">
                          {row.product_name}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{row.on_hand}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.avg_daily_sales != null ? row.avg_daily_sales.toFixed(2) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.days_of_cover != null ? row.days_of_cover.toFixed(1) : "—"}
                        </TableCell>
                        <TableCell>{severityBadge(row.severity)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.recommended_order_qty != null ? row.recommended_order_qty : "—"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {row.order_by_date ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4" />
              Category momentum
            </CardTitle>
            <p className="text-xs text-muted-foreground font-normal">
              Revenue vs previous {growth?.meta.window_days ?? windowDays}-day window (categories with sales in current
              period).
            </p>
          </CardHeader>
          <CardContent>
            <div className="max-h-[min(320px,50vh)] min-w-0 overflow-x-auto overflow-y-auto rounded-md border md:max-h-[320px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Current</TableHead>
                    <TableHead className="text-right">Previous</TableHead>
                    <TableHead className="text-right">Growth</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(growth?.category_momentum ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                        No category data (assign categories to products and record sales).
                      </TableCell>
                    </TableRow>
                  ) : (
                    growth!.category_momentum.map((c) => (
                      <TableRow key={c.category_id}>
                        <TableCell className="font-medium">{c.category_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatInrIntl(c.revenue_current_window)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatInrIntl(c.revenue_previous_window)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {c.growth_percent > 0 ? "+" : ""}
                          {c.growth_percent.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="w-4 h-4" />
              Often bought together
            </CardTitle>
            <p className="text-xs text-muted-foreground font-normal">
              Pairs of products on the same receipt in the selected window.
            </p>
          </CardHeader>
          <CardContent>
            <div className="max-h-[min(320px,50vh)] min-w-0 overflow-x-auto overflow-y-auto rounded-md border md:max-h-[320px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product A</TableHead>
                    <TableHead>Product B</TableHead>
                    <TableHead className="text-right">Times</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(growth?.basket_affinity ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center text-muted-foreground py-6">
                        Need more multi-line sales to detect pairs.
                      </TableCell>
                    </TableRow>
                  ) : (
                    growth!.basket_affinity.map((b) => (
                      <TableRow key={`${b.product_a_id}_${b.product_b_id}`}>
                        <TableCell className="min-w-0 max-w-[min(8rem,38vw)] truncate text-sm sm:max-w-[10rem]">
                          {b.product_a_name}
                        </TableCell>
                        <TableCell className="min-w-0 max-w-[min(8rem,38vw)] truncate text-sm sm:max-w-[10rem]">
                          {b.product_b_name}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{b.pair_count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4" />
            Top sellers with low cover
          </CardTitle>
          <p className="text-xs text-muted-foreground font-normal">
            High revenue in the window but under ~14 days of cover — prioritize replenishment.
          </p>
        </CardHeader>
        <CardContent>
          <div className="min-w-0 overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Revenue ({windowDays}d)</TableHead>
                  <TableHead className="text-right">Days cover</TableHead>
                  <TableHead>Severity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(growth?.top_movers_low_cover ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                      No matches yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  growth!.top_movers_low_cover.map((r) => (
                    <TableRow key={r.product_id}>
                      <TableCell className="font-medium">{r.product_name}</TableCell>
                      <TableCell className="text-right">{formatInrIntl(Number(r.revenue_window))}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.days_of_cover.toFixed(1)}</TableCell>
                      <TableCell>{severityBadge(r.severity)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm border-dashed">
        <CardHeader>
          <CardTitle className="text-base">Peer and assortment insights</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>{peer?.data.message}</p>
          <ul className="list-disc pl-5 space-y-1">
            {(peer?.data.roadmap ?? []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
